/**
 * Helpers de génération PDF spécifiques béton (Sprint LB-D) :
 * - generateDevisPDF : devis avec certificat NF C 67-200 auto pour les
 *   lignes POTEAUX_BA (mention norme + résistance théorique + classe)
 * - generateBLPDF : bon de livraison avec liste individuelle des lots
 *   (numéros L-YYYY-NNNNN traçables via QR)
 *
 * Charge jsPDF + autoTable en dynamic import (chunk séparé Vite).
 */

import type { FamilleProduit, LotProduction } from '@/types';

const round2 = (n: number) => Math.round(n * 100) / 100;
const fmt = (n: number) => round2(n).toLocaleString('fr-FR', { maximumFractionDigits: 2 });
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });

export interface ClientInfo {
  fullName:    string;
  companyName: string | null;
  email?:      string | null;
  phone?:      string | null;
  adresse?:    string;
}

export interface FilialeInfo {
  name:     string;
  fullName: string;
  pays:     string;
  devise:   string;
}

export interface LigneDevisProduit {
  code:             string;
  designation:      string;
  famille:          FamilleProduit;
  norme?:           string | null;
  classeProduit?:   string | null;
  resistanceFc28?:  string | null;  // MPa
  longueurM?:       number | null;
  effortNominalDaN?: number | null;
  quantite:         number;
  uniteVente:       string;
  prixHTUnitaireCents: number;
  remisePct?:       number;
  contratRef?:      string | null;
}

export interface DevisOptions {
  numeroDevis:   string;
  dateEmission:  string;          // YYYY-MM-DD
  validiteJours: number;          // ex: 30
  filiale:       FilialeInfo;
  client:        ClientInfo;
  lignes:        LigneDevisProduit[];
  tvaPct:        number;          // ex: 18 (Côte d'Ivoire)
  notesFooter?:  string;
}

export async function generateDevisPDF(opts: DevisOptions): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.width;

  // En-tête filiale
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 132, 92); // vert Leadbeton
  doc.text(opts.filiale.fullName, 14, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`${opts.filiale.pays} · Devise ${opts.filiale.devise}`, 14, 24);

  // Bloc DEVIS
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20);
  doc.text('DEVIS', pageW - 14, 18, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`N° ${opts.numeroDevis}`, pageW - 14, 24, { align: 'right' });
  doc.text(`Émis le ${fmtDate(opts.dateEmission)}`, pageW - 14, 29, { align: 'right' });
  doc.text(`Validité ${opts.validiteJours} jours`, pageW - 14, 34, { align: 'right' });

  // Séparateur
  doc.setDrawColor(220);
  doc.line(14, 40, pageW - 14, 40);

  // Client
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120);
  doc.text('CLIENT', 14, 48);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(20);
  doc.setFontSize(11);
  doc.text(opts.client.companyName ?? opts.client.fullName, 14, 54);
  doc.setFontSize(9);
  doc.setTextColor(80);
  let y = 60;
  if (opts.client.companyName) { doc.text(opts.client.fullName, 14, y); y += 4; }
  if (opts.client.adresse) { doc.text(opts.client.adresse, 14, y); y += 4; }
  if (opts.client.email)   { doc.text(opts.client.email, 14, y); y += 4; }
  if (opts.client.phone)   { doc.text(opts.client.phone, 14, y); y += 4; }

  // Table lignes
  const body = opts.lignes.map((l) => {
    const prixHT  = l.prixHTUnitaireCents / 100;
    const remise  = l.remisePct ?? 0;
    const totalHT = prixHT * l.quantite * (1 - remise / 100);
    return [
      l.code,
      l.designation + (l.contratRef ? `\n[Contrat: ${l.contratRef}]` : ''),
      `${fmt(l.quantite)} ${l.uniteVente}`,
      fmt(prixHT),
      remise > 0 ? `-${remise}%` : '—',
      fmt(totalHT),
    ];
  });

  autoTable(doc, {
    startY: y + 4,
    head:   [['Code', 'Désignation', 'Qté', `PU HT (${opts.filiale.devise})`, 'Remise', `Total HT (${opts.filiale.devise})`]],
    body,
    styles: { fontSize: 9, cellPadding: 2.5 },
    headStyles: { fillColor: [31, 132, 92], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 22, fontStyle: 'bold' },
      1: { cellWidth: 65 },
      2: { halign: 'right', cellWidth: 24 },
      3: { halign: 'right', cellWidth: 26 },
      4: { halign: 'center', cellWidth: 18 },
      5: { halign: 'right', cellWidth: 30, fontStyle: 'bold' },
    },
    margin: { left: 14, right: 14 },
  });

  // Totaux
  const totalHT  = opts.lignes.reduce((s, l) => s + (l.prixHTUnitaireCents / 100) * l.quantite * (1 - (l.remisePct ?? 0) / 100), 0);
  const tva      = totalHT * (opts.tvaPct / 100);
  const totalTTC = totalHT + tva;

  let afterY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 6;
  doc.setFontSize(9);
  doc.setTextColor(20);
  const labelX = pageW - 70;
  const valX   = pageW - 14;
  doc.text('Total HT',     labelX, afterY);   doc.text(`${fmt(totalHT)} ${opts.filiale.devise}`,  valX, afterY, { align: 'right' }); afterY += 5;
  doc.text(`TVA ${opts.tvaPct}%`, labelX, afterY); doc.text(`${fmt(tva)} ${opts.filiale.devise}`,      valX, afterY, { align: 'right' }); afterY += 5;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text('Total TTC',    labelX, afterY);   doc.text(`${fmt(totalTTC)} ${opts.filiale.devise}`, valX, afterY, { align: 'right' });
  doc.setFont('helvetica', 'normal');

  // Certificat NF pour les lignes POTEAUX_BA NF C 67-200
  const lignesNF = opts.lignes.filter((l) => l.famille === 'POTEAUX_BA' && l.norme === 'NF C 67-200');
  if (lignesNF.length > 0) {
    afterY += 16;
    if (afterY > 230) { doc.addPage(); afterY = 20; }
    doc.setDrawColor(31, 132, 92);
    doc.setLineWidth(0.5);
    doc.line(14, afterY, pageW - 14, afterY);
    afterY += 6;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 132, 92);
    doc.text('✓ CERTIFICAT NF C 67-200', 14, afterY);
    afterY += 5;
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);
    doc.text(
      `Le fournisseur ${opts.filiale.fullName} certifie que les poteaux ci-dessous sont conformes à la norme française NF C 67-200`,
      14, afterY,
    );
    afterY += 3.5;
    doc.text('"Poteaux pour lignes électriques aériennes" : résistance fc28 garantie ≥ 35 MPa, tolérance longueur ±5 mm,', 14, afterY);
    afterY += 3.5;
    doc.text('charge de rupture ≥ 2× effort nominal. Contrôles qualité réalisés sur chaque lot livré.', 14, afterY);
    afterY += 5;

    autoTable(doc, {
      startY: afterY,
      head: [['Code', 'Classe', 'Longueur', 'Effort nominal', 'fc28 garanti']],
      body: lignesNF.map((l) => [
        l.code,
        l.classeProduit ?? '—',
        l.longueurM ? `${l.longueurM} m` : '—',
        l.effortNominalDaN ? `${l.effortNominalDaN} daN` : '—',
        l.resistanceFc28 ? `≥ ${l.resistanceFc28} MPa` : '≥ 35 MPa',
      ]),
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [240, 250, 244], textColor: [31, 132, 92], fontStyle: 'bold', lineColor: [31, 132, 92], lineWidth: 0.3 },
      bodyStyles: { lineColor: [220, 220, 220], lineWidth: 0.1 },
      margin: { left: 14, right: 14 },
    });
  }

  // Footer
  if (opts.notesFooter) {
    const finalY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 8;
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(opts.notesFooter, 14, Math.min(finalY, 285));
  }

  doc.save(`Devis_${opts.numeroDevis}_${opts.dateEmission}.pdf`);
}

// ── BL : Bon de livraison par chantier ──────────────────────────────────────

export interface BLOptions {
  numeroBL:        string;
  dateEmission:    string;
  filiale:         FilialeInfo;
  client:          ClientInfo;
  chantierNom:     string;
  chantierAdresse?: string;
  lots: Array<LotProduction & {
    produit?: {
      code: string; designation: string; famille: FamilleProduit; norme: string | null;
      classeProduit: string | null; uniteVente: string;
    };
  }>;
  baseUrl:         string;  // pour les liens QR (ex: https://app.simgroupci.com)
}

export async function generateBLPDF(opts: BLOptions): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.width;

  // En-tête
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(31, 132, 92);
  doc.text(opts.filiale.fullName, 14, 18);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`${opts.filiale.pays}`, 14, 24);

  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(20);
  doc.text('BON DE LIVRAISON', pageW - 14, 18, { align: 'right' });
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80);
  doc.text(`N° ${opts.numeroBL}`, pageW - 14, 24, { align: 'right' });
  doc.text(`Émis le ${fmtDate(opts.dateEmission)}`, pageW - 14, 29, { align: 'right' });

  doc.setDrawColor(220);
  doc.line(14, 36, pageW - 14, 36);

  // Client + Chantier
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(120);
  doc.text('LIVRÉ À', 14, 44);
  doc.text('CHANTIER', pageW / 2 + 4, 44);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(20);
  doc.setFontSize(10);
  doc.text(opts.client.companyName ?? opts.client.fullName, 14, 49);
  if (opts.client.companyName) doc.text(opts.client.fullName, 14, 53);
  if (opts.client.adresse)     doc.text(opts.client.adresse, 14, 57);

  doc.text(opts.chantierNom, pageW / 2 + 4, 49);
  if (opts.chantierAdresse) doc.text(opts.chantierAdresse, pageW / 2 + 4, 53);

  // Table des lots livrés
  const body = opts.lots.map((l) => [
    l.lotNumber,
    l.produit?.code ?? '—',
    l.produit?.designation ?? '—',
    `${fmt(parseFloat(l.quantite))} ${l.produit?.uniteVente ?? ''}`,
    fmtDate(l.dateProduction),
    l.controleQualiteOk === true  ? '✓ Conforme'
      : l.controleQualiteOk === false ? '✗ NC'
      : '—',
    `${opts.baseUrl}/qr/lot/${l.id}`,
  ]);

  autoTable(doc, {
    startY: 68,
    head:   [['N° Lot', 'Code', 'Désignation', 'Quantité', 'Production', 'CQ', 'Traçabilité QR']],
    body,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [31, 132, 92], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 26, fontStyle: 'bold', fontSize: 8 },
      1: { cellWidth: 18 },
      2: { cellWidth: 50 },
      3: { halign: 'right', cellWidth: 22 },
      4: { cellWidth: 20 },
      5: { halign: 'center', cellWidth: 18 },
      6: { fontSize: 6, textColor: [100, 116, 139] },
    },
    margin: { left: 14, right: 14 },
  });

  const totalQte = opts.lots.reduce((s, l) => s + parseFloat(l.quantite), 0);
  let afterY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY + 4;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`Total : ${opts.lots.length} lot${opts.lots.length > 1 ? 's' : ''} · ${fmt(totalQte)} unités livrées`, 14, afterY);

  // Mention NF si des lots POTEAUX_BA NF C 67-200
  const lotsNF = opts.lots.filter((l) => l.produit?.famille === 'POTEAUX_BA' && l.produit?.norme === 'NF C 67-200');
  if (lotsNF.length > 0) {
    afterY += 10;
    doc.setDrawColor(31, 132, 92);
    doc.line(14, afterY, pageW - 14, afterY);
    afterY += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(31, 132, 92);
    doc.text('✓ Lots conformes NF C 67-200', 14, afterY);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60);
    doc.setFontSize(8);
    afterY += 4;
    doc.text(`${lotsNF.length} lot${lotsNF.length > 1 ? 's' : ''} de poteaux BA livré${lotsNF.length > 1 ? 's' : ''} avec contrôles fc28, longueur et charge rupture conformes.`, 14, afterY);
    afterY += 3.5;
    doc.text('Scanner le QR code de chaque lot pour accéder à sa fiche traçabilité complète.', 14, afterY);
  }

  // Signature
  afterY = Math.max(afterY, 240);
  doc.setFontSize(9);
  doc.setTextColor(60);
  doc.text('Réceptionné par (nom + signature) :', 14, afterY);
  doc.setDrawColor(180);
  doc.rect(14, afterY + 4, 80, 25);
  doc.text('Livreur :', pageW - 80, afterY);
  doc.rect(pageW - 14 - 80, afterY + 4, 80, 25);

  doc.save(`BL_${opts.numeroBL}_${opts.dateEmission}.pdf`);
}
